import {
  Button,
  Card,
  Carousel,
  Divider,
  Grid,
  GridGallery,
  Heading,
  ProductImageGallery,
  ReviewCard,
  Tile,
  TileContent,
  TileMedia,
  TileText,
  TileTitle,
} from '@design-system';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { AnalyticsObjectProvider } from '@utils/analytics/object';
import { cn } from '@utils/cn';
import { isPrefersReducedMotion } from '@utils/isPrefersReducedMotion';
import {
  getFirstStoryblokParagraph,
  isStoryblokRichTextPresent,
} from '@utils/renderStoryblokRichText';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import SbEditable from 'storyblok-react';
import { render } from 'storyblok-rich-text-react-renderer-ts';

import { WORK_PAGE_TYPE } from '../../constants';
import { FF_PRODUCT_STICKY_CTA_BOTTOM } from '../../constants/featureFlags';
import {
  PRODUCT_TAG_MEDIUM_COLLECTIBLE,
  PRODUCT_TAG_MEDIUM_NFT,
  PRODUCT_TAG_MEDIUM_PRINT,
  PRODUCT_TAG_MEDIUM_SCULPTURE,
  PRODUCT_TAG_MEDIUM_STUDIO_WORKS,
} from '../../constants/product';
import { ImageSizes } from '../../helpers/images';
import {
  getProductRef,
  getProductStatus,
  presentedPurchasingState,
  productStatusFor,
  purchasingState,
} from '../../helpers/purchasing';
import { useIsSubscribed } from '../../hooks/subscription/useIsSubscribed';
import { useDrawStage } from '../../hooks/useDrawStage';
import { useIsPreOrder } from '../../hooks/useIsPreOrder';
import { useDraws } from '../../providers/DrawProvider';
import { PreAuthOfferProvider } from '../../providers/PreAuthOfferProvider';
import { usePurchase } from '../../providers/PurchaseProvider';
import { MultiMediaClickOptions } from '../../types/Analytics';
import { AugmentedArtistCard } from '../../types/Artists';
import { ProductGateway } from '../../types/AvantArteProduct';
import { Article } from '../../types/Franchises';
import {
  GeneratedReview,
  GeneratedSubscriptionType,
} from '../../types/generated';
import {
  ProductV2Storyblok,
  SeriesStoryblok,
} from '../../types/generated-storyblok';
import { CurrencyCode } from '../../types/Locations';
import { ProductStructure } from '../../types/Products';
import {
  ProductPurchaseDetails,
  ProductPurchasingController,
  PurchasingState,
} from '../../types/Purchasing';
import {
  getStandardProductProperties,
  TrackCarouselSlide,
  TrackLinkClicked,
  TrackMultiMediaClick,
  TrackWorkPageView,
  transformProductToEventObject,
} from '../../utils/analytics';
import { useAnalyticsEmitter } from '../../utils/analytics/emitter';
import {
  getArtistTileOverline,
  renderArtistsNamesFromPageContent,
  renderAugmentedArtistsNames,
} from '../../utils/artists';
import { framedByDefault } from '../../utils/frames';
import { getArtistTag } from '../../utils/getArtistTag';
import {
  isAuctionRelease,
  isReleaseTypeBlockchainAuction,
} from '../../utils/product';
import {
  shopifyAdminIdToGraphqlId,
  shopifyGraphqlIdToAdminId,
} from '../../utils/shopifyIds';
import { storyblokProductReleaseType } from '../../utils/storyblokReleaseType';
import { Connect } from '../Auction/Manifold/widgets/Connect';
import { Price as AuctionPrice } from '../Auction/Manifold/widgets/Price';
import ProductDate from '../Containers/ProductDate';
import {
  getStickyCtaUrgency,
  StickyBottomCta,
  useFullyInView,
  useStickyCtaDismissed,
} from '../Containers/StickyBottomCta';
import StickyHeader from '../Containers/StickyHeader';
import { ArtistSubscription } from '../Content/ArtistSubscription/ArtistSubscription';
import MetaTags from '../Content/MetaTags';
import { ProductAbstractV2 } from '../Content/ProductAbstract/ProductAbstractV2';
import ProductLd from '../Content/ProductLD';
import { ProductRecommendations } from '../Content/ProductRecommendations';
import ProductsCarousel from '../Content/ProductsCarousel';
import { Filter } from '../Content/ProductsCarousel/ProductsCarousel';
import { ContentBlocks } from '../ContentBlocks';
import { ContentBlockVideo } from '../ContentBlockVideo';
import { Image } from '../Image';
import PageSection from '../Layout/PageSection';
import { ProductPrice } from '../ProductPrice/ProductPrice';
import { ProductStickyButtons } from '../ProductStickyButtons/ProductStickyButtons';
import ProductTile from '../ProductTile';
import ScrollAnchor from '../ScrollAnchor';
import { ShareButtons } from '../Share/ShareButtons';

type Props = {
  blok: ProductType;
  aaProductsGateway: ProductGateway;
  augmentedArtists: AugmentedArtistCard[];
  productDetails: ProductPurchaseDetails | null;
  recommendations: MultiCurrencyProduct[];
  reviews: GeneratedReview[];
};

type SeriesStory = {
  slug: string;
  full_slug: string;
  content: SeriesStoryblok;
};

const ProductV2: React.FC<Props> = ({
  aaProductsGateway,
  augmentedArtists,
  blok,
  productDetails,
  recommendations,
  reviews,
}) => {
  const { t } = useTranslation([
    'product',
    'artists',
    'common',
    'orderDetails',
  ]);
  const heroImageRef = useRef<HTMLAnchorElement>(null);

  const purchasingController = usePurchase();

  const { mainDraw } = useDraws();
  const drawStage = useDrawStage(mainDraw?.draw);
  const isPreOrder = useIsPreOrder(mainDraw?.draw);

  const analytics = useMemo(
    () => ({
      context: 'product page',
      page: WORK_PAGE_TYPE,
      ...transformProductToEventObject(
        getStandardProductProperties(
          blok,
          purchasingController,
          drawStage,
          // if draw is a pre-order or the draw has pre-order opt ins enabled
          isPreOrder || !!mainDraw?.draw?.preorderEnabled,
        ),
      ),
    }),
    [blok, purchasingController, drawStage, isPreOrder, mainDraw?.draw],
  );

  const [analyticsEmitter] = useAnalyticsEmitter(analytics);

  usePageViewAnalytics(blok, purchasingController);

  if (blok.component === 'product') {
    return null;
  }

  const highlightVideo = (blok?.highlightVideo ?? []).map((blok) => {
    return { ...blok, analytics };
  });

  const artistContent = (
    blok as unknown as ProductV2Storyblok
  )?.artistContent?.map((article) => {
    return { ...article, analytics };
  }) as Article;

  const artists = (() => {
    if (!blok.productArtist) return [];

    return blok.productArtist.map((a: ResolvedRelatedArtist) => a.artist);
  })();

  const relatedSeries = (
    blok.relatedSeries?.[0] as { series?: SeriesStory } | undefined
  )?.series;

  const relatedSeriesProducts =
    (relatedSeries?.content.series?.[0]?.products as Product[] | undefined) ??
    [];

  const { checkIsSubscribed, refetch } = useIsSubscribed(
    relatedSeriesProducts.map((product) => ({
      type: GeneratedSubscriptionType.Product,
      objectId: shopifyGraphqlIdToAdminId(product.id) || '',
    })),
  );

  const productImageGalleryRef = useRef<HTMLAnchorElement>(null);
  const scrollToProductImageGallery = () => {
    productImageGalleryRef.current?.scrollIntoView({
      behavior: isPrefersReducedMotion() ? 'instant' : 'smooth',
    });
  };
  const [resetImageGallery, setResetImageGallery] = useState(0);

  const images = useMemo(() => {
    if (!purchasingController.frame) {
      return blok.images;
    }

    setResetImageGallery((prev) => prev + 1);
    scrollToProductImageGallery();

    // There is a convention followed by the content team that the first image
    // on the frame product is a frame by itself, and the second one is
    // a composite image of a product in a selected frame.
    return purchasingController.frame?.images.length > 1
      ? [
          {
            component: 'image' as const,
            image: {
              id: Math.floor(Math.random() * Number.MAX_SAFE_INTEGER),
              alt: purchasingController.frame.title,
              copyright: '',
              filename: purchasingController.frame.images[1].src,
              name: '',
              title: '',
              fieldtype: '',
              focus: undefined,
            },
            showCopyright: false,
            _uid: purchasingController.frame.variant.consolidatedVariants[0]
              .shopifyFrameVariantId,
          },
          ...(blok.images?.slice(1) ?? []),
        ]
      : blok.images;
  }, [purchasingController.frame, blok.images]);

  useEffect(() => {
    if (purchasingController.frame) {
      scrollToProductImageGallery();
    }
  }, [purchasingController.frame?.title]);

  const ctaScrollAnchorRef = useRef<HTMLAnchorElement>(null);
  const transactionBlockRef = useRef<HTMLDivElement>(null);
  const ctaOpenCheckoutDrawerRef = useRef<{
    openCheckoutDrawer: null | (() => void);
  }>(null);

  /**
   * Derived during render rather than in an effect: the sticky CTA renders
   * nothing for `UNKNOWN`, so settling this a render late cost it the first
   * frame (PTE-4262). `purchasingController.product` comes from the page's own
   * props, so it is already here on the first render.
   */
  const productState: PurchasingState = useMemo(
    () =>
      purchasingController.product
        ? purchasingState({
            releaseType: blok.releaseType,
            closingDate: blok.closingDate,
            available: purchasingController.product.available,
            tags: purchasingController.product.tags,
          })
        : 'UNKNOWN',
    [blok, purchasingController],
  );
  const presentedState = presentedPurchasingState(productState);
  const productStatus = productStatusFor(productState);
  const releaseType = storyblokProductReleaseType(blok);

  /**
   * PTE-4262: on mobile the sticky CTA moves to the bottom of the viewport and
   * gets out of the way only once the whole transactional block is on screen,
   * returning as soon as any of it scrolls back out. Desktop keeps the bar
   * pinned under the header.
   */
  const experimentalStickyCtaBottom = useFeatureIsOn(
    FF_PRODUCT_STICKY_CTA_BOTTOM,
  );
  const stickyCta = useStickyCtaDismissed({
    draw: mainDraw?.draw,
    enabled: experimentalStickyCtaBottom,
    isPreOrder,
    product: blok as unknown as ProductType,
    productRef: getProductRef(purchasingController),
  });
  const transactionBlockFullyInView = useFullyInView(transactionBlockRef);
  const stickyCtaUrgency = getStickyCtaUrgency({
    closingDate: blok.closingDate,
    draw: mainDraw?.draw,
    drawStage,
    launchDate: blok.launchDate,
    productState,
    releaseType,
  });
  const productIsArtistBag = productDetails?.tags
    .map((t) => t.toLowerCase())
    .includes('artist bag');

  const releaseCollectionGalleryRef = useRef<HTMLDivElement>(null);
  const releaseSeriesGalleryRef = useRef<HTMLDivElement>(null);

  const abstractGalleryRef =
    blok.releaseCollectionGallery?.length > 0
      ? releaseCollectionGalleryRef
      : relatedSeriesProducts.length > 0
        ? releaseSeriesGalleryRef
        : undefined;

  const blockChainAuction = blok.nftAuction?.[0];

  const price = useMemo(() => {
    if (!purchasingController.product) return null;

    if (
      productState === 'COMING_SOON' &&
      isReleaseTypeBlockchainAuction(releaseType) &&
      blockChainAuction?.floorPrice
    ) {
      return `${t('auction.from')} ${blockChainAuction?.floorPrice}`;
    }

    if (
      productState === 'AVAILABLE' &&
      isReleaseTypeBlockchainAuction(releaseType) &&
      blockChainAuction?.manifoldListingId
    ) {
      return (
        <span className="product-tile">
          <Connect className="hidden" />
          <AuctionPrice
            defaultValue={blockChainAuction.floorPrice}
            isRanked={releaseType === 'nft_ranked_auction'}
            listingId={blockChainAuction.manifoldListingId.toString()}
            prefix={t('auction.from')}
          />
        </span>
      );
    }

    if (isAuctionRelease(releaseType) && mainDraw?.draw?.auctionReservePrice) {
      return (
        <ProductPrice
          currencyCode={
            mainDraw.draw.auctionReservePrice.currencyCode as CurrencyCode
          }
          price={Number(mainDraw.draw.auctionReservePrice.amount)}
          skipConvertCurrency
          skipTaxCalculation
          suffix={t('auction.default.reservePriceAsSuffix')}
        />
      );
    }

    return <ProductPrice price={purchasingController.product.price} />;
  }, [
    mainDraw?.draw?.auctionReservePrice,
    blockChainAuction?.floorPrice,
    blockChainAuction?.manifoldListingId,
    productState,
    purchasingController.product,
    releaseType,
    t,
  ]);

  const onViewAsSetHandler =
    (purchasingController.collectionSet?.length ?? 0) > 1 &&
    typeof ctaOpenCheckoutDrawerRef.current?.openCheckoutDrawer === 'function'
      ? () => {
          purchasingController.collectionSetToggleAll();
          ctaOpenCheckoutDrawerRef?.current?.openCheckoutDrawer?.();
        }
      : undefined;

  return (
    <AnalyticsObjectProvider name="product page" properties={analytics}>
      {() => (
        <PreAuthOfferProvider
          productId={blok.aaId}
          productVariantId={blok.shopifyVariantId}
        >
          <ProductLd product={getProductLd(blok, purchasingController)} />
          {blok.excludeFromIndex && <MetaTags robots="noindex" />}
          {/* @ts-expect-error require storyblok-react upgrade */}
          <SbEditable content={blok}>
            <div>
              {presentedState !== 'SOLD_OUT' && purchasingController.product ? (
                <StickyHeader
                  className={cn(
                    experimentalStickyCtaBottom && 'hidden md:block',
                  )}
                  ctaRef={heroImageRef}
                >
                  <div className="flex w-full items-center space-x-3">
                    <div className="h-12 w-12 shrink-0 rounded-sm">
                      <Image
                        alt={blok.thumbnail.alt}
                        focus={blok.thumbnail.focus}
                        shape="rounded"
                        src={blok.thumbnail.filename}
                        width={48}
                      />
                    </div>
                    <div className="flex-1 overflow-hidden">
                      <p className="truncate">{blok.title}</p>
                      <p className="truncate text-neutral-4">{price}</p>
                    </div>
                    <ProductStickyButtons
                      anchorRef={ctaScrollAnchorRef}
                      draw={mainDraw?.draw}
                      product={blok}
                      productRef={getProductRef(purchasingController)}
                      productState={presentedState}
                      productStatus={productStatus}
                      releaseType={releaseType}
                    />
                  </div>
                </StickyHeader>
              ) : null}

              {experimentalStickyCtaBottom &&
              stickyCta.settled &&
              !stickyCta.dismissed &&
              purchasingController.product ? (
                <StickyBottomCta
                  urgency={
                    stickyCtaUrgency ? (
                      <ProductDate {...stickyCtaUrgency.dateProps} />
                    ) : null
                  }
                  urgencyTone={stickyCtaUrgency?.tone}
                  visible={!transactionBlockFullyInView}
                >
                  <ProductStickyButtons
                    anchorRef={ctaScrollAnchorRef}
                    bottomBar
                    buttonLocation="product page sticky bottom"
                    draw={mainDraw?.draw}
                    fullWidth={!stickyCtaUrgency}
                    product={blok}
                    productRef={getProductRef(purchasingController)}
                    productState={presentedState}
                    productStatus={productStatus}
                    releaseType={releaseType}
                  />
                </StickyBottomCta>
              ) : null}

              <main
                className={cn(
                  'mb-9 mt-0 space-y-9 md:my-12 md:space-y-12',
                  experimentalStickyCtaBottom && 'pb-24 md:pb-0',
                )}
              >
                <PageSection fullWidth>
                  <div className="md:px-[var(--website-absolute-padding)]">
                    <div className="grid-section gap-y-6">
                      <div className="col-span-12 md:col-span-6">
                        <ScrollAnchor
                          ref={productImageGalleryRef}
                          id="productImageGallery"
                          offset={-72}
                        />
                        {images?.length > 0 && (
                          <div className="sticky top-0 sm:top-30">
                            {onViewAsSetHandler ? (
                              <div className="absolute right-3 top-10 z-10 md:right-5 md:top-5">
                                <Button
                                  disabled={purchasingController.loading}
                                  label={t('collectionSet.viewAsSet')}
                                  onClick={() => {
                                    analyticsEmitter(
                                      'product page',
                                      'view set clicked',
                                      {
                                        location: 'image gallery',
                                      },
                                    );
                                    onViewAsSetHandler();
                                  }}
                                  size="sm"
                                  variant="tertiary"
                                />
                              </div>
                            ) : null}
                            <ProductImageGallery
                              heroImageRef={heroImageRef}
                              images={images}
                              onClick={(properties: MultiMediaClickOptions) => {
                                TrackMultiMediaClick({
                                  ...properties,
                                  page: WORK_PAGE_TYPE,
                                  pageTitle: blok.title,
                                  context: 'product image gallery',
                                });
                              }}
                              resetTrigger={resetImageGallery}
                            />
                            <div className="splide-skeleton">
                              {images.slice(0, 1).map((media) => {
                                const image =
                                  media.component === 'Video'
                                    ? media.coverImage
                                    : media.image;
                                const sizes = {
                                  sm: { ratio: '4/5', span: 12 },
                                  md: { ratio: '1/1', span: 6 },
                                  lg: { ratio: '1/1', span: 6 },
                                } as ImageSizes;

                                return (
                                  <Image
                                    key={image.id}
                                    alt={image.alt}
                                    eagerLoading
                                    focus={image.focus}
                                    hoverEffect
                                    shape="rounded"
                                    size={sizes}
                                    src={image.filename}
                                  />
                                );
                              })}
                              <div className="splide__pagination w-full gap-2 pt-4 md:!hidden">
                                <div className="splide__pagination__page block h-1.5 w-1.5 animate-pulse rounded-full bg-neutral-2" />
                                <div className="splide__pagination__page block h-1.5 w-1.5 animate-pulse rounded-full bg-neutral-2" />
                                <div className="splide__pagination__page block h-1.5 w-1.5 animate-pulse rounded-full bg-neutral-2" />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="col-span-12 md:col-span-6">
                        <div className="sticky top-0 mx-auto w-full max-w-md space-y-9 px-5 sm:top-30 md:space-y-12 md:px-0">
                          <ProductAbstractV2
                            analytics={analytics}
                            blok={blok as unknown as ProductV2Storyblok}
                            ctaOpenCheckoutDrawerRef={ctaOpenCheckoutDrawerRef}
                            ctaScrollAnchorRef={ctaScrollAnchorRef}
                            galleryRef={abstractGalleryRef}
                            onViewAsSetHandler={onViewAsSetHandler}
                            productsGateway={aaProductsGateway}
                            productState={productState}
                            purchasingController={purchasingController}
                            transactionBlockRef={transactionBlockRef}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </PageSection>

                {highlightVideo?.length > 0 && (
                  <PageSection className="hidden md:block">
                    <ContentBlockVideo blok={highlightVideo[0]} />
                  </PageSection>
                )}

                <PageSection>
                  <ContentBlocks content={artistContent} />
                </PageSection>

                {relatedSeries?.content && (
                  <div
                    ref={releaseSeriesGalleryRef}
                    className="before:width-full before:invisible before:-z-90 before:-mt-40 before:block before:h-40 before:content-['']"
                  >
                    <PageSection>
                      <h2 className="neutral-4 relative z-20 mb-4 text-md md:text-lg">
                        {t('seriesTitle')}
                      </h2>

                      <div className="grid-section gap-y-6">
                        {relatedSeriesProducts.map((product, index) => (
                          <article
                            key={product.id}
                            className="col-span-6 md:col-span-4 lg:col-span-3"
                          >
                            <ProductTile
                              analytics={
                                analytics && {
                                  page: analytics.page,
                                  context: analytics.context,
                                  position: index,
                                }
                              }
                              isSubscribed={checkIsSubscribed(
                                shopifyGraphqlIdToAdminId(product.id),
                                GeneratedSubscriptionType.Product,
                              )}
                              onSubscribe={() => {
                                refetch();
                              }}
                              product={product}
                            />
                          </article>
                        ))}
                      </div>
                    </PageSection>
                  </div>
                )}

                {blok.releaseCollectionGallery?.length > 0 && (
                  <div
                    ref={releaseCollectionGalleryRef}
                    className="before:width-full before:invisible before:-z-90 before:-mt-40 before:block before:h-40 before:content-['']"
                  >
                    <PageSection>
                      <section className="space-y-4">
                        <Heading level="h2">
                          {t('releaseCollectionGalleryHeading')}
                        </Heading>
                        <GridGallery images={blok.releaseCollectionGallery} />
                      </section>
                    </PageSection>
                  </div>
                )}

                {artists?.length > 0 && (
                  <PageSection>
                    <Grid className="gap-y-6" columns={4}>
                      {artists.map((artist) => {
                        const augmentedArtist = augmentedArtists.find(
                          (value) => value.slug === artist.slug,
                        );

                        if (!augmentedArtist) return null;

                        return (
                          <Card key={artist.id}>
                            <Tile className="group gap-4" direction="column">
                              <TileMedia
                                className="rounded-none"
                                overlay={
                                  <>
                                    {artist.internalId ? (
                                      <ArtistSubscription
                                        buttonLocation="product page"
                                        internalId={artist.internalId}
                                        name={artist?.name}
                                        thumbnail={
                                          artist.content.thumbnail.filename
                                        }
                                      />
                                    ) : null}
                                  </>
                                }
                              >
                                <Image
                                  src={artist.content.thumbnail.filename}
                                  focus={artist.content.thumbnail.focus}
                                  alt={artist.content.thumbnail.alt}
                                  hoverEffect
                                  size={{
                                    sm: { ratio: '4/3', span: 12 },
                                    md: { ratio: '4/3', span: 6 },
                                    lg: { ratio: '4/3', span: 4 },
                                  }}
                                />
                              </TileMedia>

                              <TileContent
                                className="w-full px-4 pb-4"
                                direction="column"
                              >
                                <TileTitle>
                                  <Link
                                    href={`/artists/${artist.slug}`}
                                    onClick={() => {
                                      void TrackLinkClicked({
                                        page: WORK_PAGE_TYPE,
                                        context: 'artist tile',
                                        title: artist.name || '',
                                        url: artist.slug || '',
                                        isExternal: false,
                                      });
                                    }}
                                    prefetch={false}
                                    className="after:absolute after:inset-0 after:block after:content-['']"
                                  >
                                    {artist.name}
                                  </Link>
                                </TileTitle>
                                <TileText>
                                  {getArtistTileOverline(
                                    augmentedArtist.totalWorks,
                                    augmentedArtist.hideCollaborations,
                                    t,
                                  )}
                                </TileText>

                                {isStoryblokRichTextPresent(
                                  artist.content.practiceOverview,
                                ) ? (
                                  <TileText
                                    className="mt-3 line-clamp-3"
                                    variant="muted"
                                  >
                                    {render(
                                      getFirstStoryblokParagraph(
                                        artist.content.practiceOverview,
                                      ),
                                      {
                                        markResolvers: {
                                          link: (children) => <>{children}</>,
                                        },
                                        nodeResolvers: {
                                          paragraph: (children) => (
                                            <>{children}</>
                                          ),
                                        },
                                      },
                                    )}
                                  </TileText>
                                ) : null}
                              </TileContent>
                            </Tile>
                          </Card>
                        );
                      })}
                    </Grid>
                  </PageSection>
                )}

                <PageSection>
                  <div className="flex flex-col gap-y-4">
                    <div className="flex flex-col text-center">
                      <p className="font-bold">{t('product:shareTitle')}</p>
                      <p>{t('product:shareBody')}</p>
                    </div>

                    <ShareButtons
                      className="flex items-center justify-center gap-2"
                      onClick={(platform) =>
                        analyticsEmitter('share', platform)
                      }
                      text={blok.title}
                      utmCampaign="product-page"
                    />
                  </div>
                </PageSection>

                {artists.length > 0 && (
                  <ProductsCarousel
                    analytics={{
                      page: WORK_PAGE_TYPE,
                      context: t('product:moreWorksHeading', {
                        artist: renderAugmentedArtistsNames(augmentedArtists),
                      }),
                    }}
                    filter={{
                      operation: 'OR',
                      items: artists.map((a) => getArtistTag(a)),
                    }}
                    heading={t(
                      artists.length > 1
                        ? 'product:moreWorksHeadingPlural'
                        : 'product:moreWorksHeading',
                    )}
                    numberOfProducts={100}
                    order="DESC"
                    productToExclude={
                      blok.id?.[0]?.id
                        ? shopifyAdminIdToGraphqlId(blok.id[0].id)
                        : void 0
                    }
                  />
                )}

                {reviews.length > 3 && (
                  <div className="wrapper-website">
                    <Divider className="my-9 md:my-12" />
                  </div>
                )}

                {reviews.length > 3 && (
                  <PageSection fullWidth>
                    <Carousel
                      headline={t('reviews.title')}
                      items={reviews.map((review) => (
                        <ReviewCard key={review.id} review={review} />
                      ))}
                      onSlide={(index) => {
                        TrackCarouselSlide({
                          page: 'product',
                          context: 'reviews',
                          title: t('reviews.title'),
                          position: index,
                          carouselLength: reviews.length,
                        });
                      }}
                      perPage={4}
                    />
                  </PageSection>
                )}
                <div className="wrapper-website">
                  <Divider className="my-9 md:my-12" />
                </div>
                {productIsArtistBag ? (
                  <PageSection fullWidth>
                    <ProductsCarousel
                      analytics={{
                        page: WORK_PAGE_TYPE,
                        context: 'artist bag recommendations',
                      }}
                      filter={createArtistBagsCarouselFilter(
                        artists.map((a) => getArtistTag(a)),
                      )}
                      heading={t('common:recommendations.titles.default')}
                      numberOfProducts={5}
                      order="DESC"
                      productToExclude={
                        blok.id?.[0]?.id
                          ? shopifyAdminIdToGraphqlId(blok.id[0].id)
                          : void 0
                      }
                    />
                  </PageSection>
                ) : (
                  <PageSection>
                    <ProductRecommendations
                      analytics={{
                        page: WORK_PAGE_TYPE,
                        context: 'more works',
                      }}
                      mediumTag={
                        blok.medium?.includes('print')
                          ? PRODUCT_TAG_MEDIUM_PRINT
                          : blok.medium?.includes('sculpture')
                            ? PRODUCT_TAG_MEDIUM_SCULPTURE
                            : blok.medium?.includes('collectible')
                              ? PRODUCT_TAG_MEDIUM_COLLECTIBLE
                              : blok.medium?.includes('nft')
                                ? PRODUCT_TAG_MEDIUM_NFT
                                : blok.medium?.includes('studioworks')
                                  ? PRODUCT_TAG_MEDIUM_STUDIO_WORKS
                                  : undefined
                      }
                      recommendations={recommendations}
                    />
                  </PageSection>
                )}
              </main>
            </div>
          </SbEditable>
        </PreAuthOfferProvider>
      )}
    </AnalyticsObjectProvider>
  );
};

export default ProductV2;

function createArtistBagsCarouselFilter(artistNames: string[]): Filter {
  if (artistNames.length === 0) {
    return {
      operation: 'AND',
      exclude: false,
      items: ['Artist bag'],
    };
  }

  return {
    operation: 'AND',
    items: [
      'Artist bag',
      {
        operation: 'AND',
        exclude: true,
        items: artistNames,
      },
    ],
  };
}

const usePageViewAnalytics = (
  product: ProductType,
  purchasingController: ProductPurchasingController<any>,
) => {
  const { mainDraw } = useDraws();

  const drawStage = useDrawStage(mainDraw?.draw);
  const productPreOrderAvailable = useIsPreOrder(mainDraw?.draw);
  const [hasSent, setHasSent] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (purchasingController.loading) return;
    if (hasSent === product.id[0].id) return;

    TrackWorkPageView({
      drawStage,
      page: WORK_PAGE_TYPE,
      product,
      productPreOrderAvailable,
      productCanBeFramed: !!purchasingController.frameOptions?.length,
      frameIncluded: framedByDefault(
        purchasingController.product?.framingOffered,
      ),
      frameUpgradeAvailable: Boolean(purchasingController.frameOptions),
      productStatus: getProductStatus(product, purchasingController),
      price: purchasingController.product?.price,
    });

    product.id && setHasSent(product.id[0].id);
  }, [
    drawStage,
    hasSent,
    product,
    productPreOrderAvailable,
    purchasingController,
    purchasingController.loading,
    purchasingController.product,
  ]);
};

const getProductLd = (
  product: ProductType,
  purchasingController: ProductPurchasingController<any>,
): ProductStructure => {
  const router = useRouter();
  return {
    name: product.title,
    image: product.thumbnail.filename,
    primaryImageOfPage: product.thumbnail.filename,
    description: product.seo.description,
    artMedium: product.medium,
    artistName: renderArtistsNamesFromPageContent(product.productArtist),
    ...(product.productArtist &&
      product.productArtist.length > 0 && {
        artistUrl: new URL(
          (product.productArtist[0] as any).artist.full_slug,
          process.env.NEXT_PUBLIC_HOSTNAME,
        ).toString(),
      }),
    pricesAndAvailability: product.pricesAndAvailability,
    availability: getProductStatus(product, purchasingController),
    url: new URL(router.asPath, process.env.NEXT_PUBLIC_HOSTNAME).toString(),
  };
};
